// frontend/src/components/QBCustomerSelect.js
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Select, Input } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import './QBCustomerSelect.css';

/**
 * Reusable QuickBooks Customer Select component with hierarchical display,
 * dropdown search, and parent chain inclusion in search results.
 * 
 * @param {Object} props
 * @param {Array} props.hierarchicalOptions - Pre-built hierarchical options
 * @param {Map} props.optionMap - Pre-built option ID to option map
 * @param {number|null} props.value - Currently selected QB customer option ID
 * @param {Function} props.onChange - Callback when selection changes: (value) => void
 * @param {Function} props.onClear - Optional callback when selection is cleared
 * @param {string} props.placeholder - Placeholder text for the select
 * @param {Object} props.style - Additional styles for the select component
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {boolean} props.loading - Whether the select is in loading state
 * @param {boolean} props.allowClear - Whether to show clear button (default: true)
 * @param {string} props.className - Additional CSS class name
 */
const QBCustomerSelect = ({
  hierarchicalOptions,
  optionMap,
  value,
  onChange,
  onClear,
  placeholder = "Select QB customer",
  style = {},
  disabled = false,
  loading = false,
  allowClear = true,
  className = ''
}) => {
  const [dropdownSearchValue, setDropdownSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Create a function to get IDs that should be visible based on search
  const getVisibleIds = useCallback((searchInput) => {
    if (!searchInput) return null; // null means show all
    
    const searchTerms = searchInput.toLowerCase().trim().split(/\s+/);
    const visibleIds = new Set();
    
    // Find all matching items
    hierarchicalOptions.forEach(option => {
      const fullPath = (option?.fullPath ?? '').toLowerCase();
      const matches = searchTerms.every(term => fullPath.includes(term));
      
      if (matches) {
        // Add this item
        visibleIds.add(option.value);
        
        // Add all ancestors by traversing up using parent_id (O(depth) instead of O(n))
        let currentOption = option;
        while (currentOption.parentId) {
          const parent = optionMap.get(currentOption.parentId);
          if (parent) {
            visibleIds.add(parent.value);
            currentOption = parent;
          } else {
            break; // Parent not found, stop traversal
          }
        }
      }
    });
    
    return visibleIds;
  }, [hierarchicalOptions, optionMap]);

  // Filter options based on dropdown search
  const filteredHierarchicalOptions = useMemo(() => {
    if (!dropdownSearchValue) return hierarchicalOptions;
    
    const visibleIds = getVisibleIds(dropdownSearchValue);
    if (!visibleIds) return hierarchicalOptions;
    
    return hierarchicalOptions.filter(option => visibleIds.has(option.value));
  }, [dropdownSearchValue, hierarchicalOptions, getVisibleIds]);
  
  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      // Small delay to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isDropdownOpen]);

  const renderSelectedLabel = (value, fallbackLabel) => {
    const option = hierarchicalOptions.find(opt => opt.value === value);
    if (!option) {
      return <div style={{ padding: '4px 0', lineHeight: '1.5' }}>{fallbackLabel}</div>;
    }

    const levels = [...(option.parentPath || []), option.itemName].filter(Boolean);

    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '4px', 
        padding: '6px 0', 
        lineHeight: '1.5', 
        width: '100%',
        minHeight: 'fit-content'
      }}>
        {levels.map((level, index) => {
          const isLeaf = index === levels.length - 1;
          return (
            <span
              key={`${option.value}-${index}`}
              style={{
                color: isLeaf ? 'inherit' : '#8c8c8c',
                fontWeight: isLeaf ? 600 : 400,
                fontSize: isLeaf ? '14px' : '13px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: 'block'
              }}
            >
              {level}
            </span>
          );
        })}
      </div>
    );
  };
  
  return (
    <Select
      value={value}
      onChange={(newValue) => {
        setDropdownSearchValue(''); // Clear search on selection
        onChange(newValue);
      }}
      onDropdownVisibleChange={(open) => {
        setIsDropdownOpen(open);
        if (!open) {
          setDropdownSearchValue(''); // Clear search when dropdown closes
        }
      }}
      onClear={() => {
        setDropdownSearchValue(''); // Clear search on clear
        if (onClear) {
          onClear();
        } else {
          onChange(null);
        }
      }}
      placeholder={placeholder}
      className={`qb-customer-select ${className}`}
      style={{ width: '100%', overflow: 'visible', ...style }}
      popupMatchSelectWidth={true}
      showSearch={false}
      options={filteredHierarchicalOptions}
      optionRender={(option) => option.data.label}
      labelRender={(props) => renderSelectedLabel(props.value, props.label)}
      allowClear={allowClear}
      virtual={true}
      listHeight={400}
      disabled={disabled}
      loading={loading}
      suffixIcon={<DownOutlined />}
      dropdownRender={(menu) => (
        <>
          <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <Input
              ref={searchInputRef}
              placeholder="Search customers..."
              value={dropdownSearchValue}
              onChange={(e) => setDropdownSearchValue(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
              onKeyDown={(e) => {
                // Prevent dropdown from closing on Enter in search
                if (e.key === 'Enter') {
                  e.stopPropagation();
                }
              }}
            />
          </div>
          {menu}
        </>
      )}
    />
  );
};

export default QBCustomerSelect;
